/**
 * @file filters.ts
 * @description
 * -----------------------------------------------------
 * Archivo que contiene funciones para manejar filtros
 * -----------------------------------------------------
 * @version 0.0.1a
 * @created 2025-05-26
 * @author DLTCode
 * @copyright © DLTCode
 * @license Private
 * */

import { uncapitalize, capitalize } from "@/lib/text";

export type CreateFiltersResult<
  T extends Record<string, unknown>,
  P extends string | undefined,
> = {
  [K in keyof T as P extends undefined
    ? K
    : Uncapitalize<`${P}${Capitalize<string & K>}`>]: T[K];
};

/**
 * Crea un objeto de filtros a partir de un objeto dado, añadiendo (o no) un prefijo a las claves, correctamente capitalizado.
 * @param filters Objeto con los filtros a crear.
 * @param prefix Prefijo opcional que se añadirá a las claves de los filtros.
 * @returns Objeto con los filtros creados, con las claves capitalizadas y el prefijo añadido si se proporciona.
 * @example
 * ```ts
 * import { createFilters } from "@/lib/filters";
 * const filters = createFilters({ name: "John", age: 30 }, "user");
 * // Resultado: { userName: "John", userAge: 30 }
 *
 * const filters = createFilters({ name: "John", age: 30 });
 * // Resultado: { name: "John", age: 30 }
 */
export function createFilters<
  T extends Record<string, unknown> = Record<string, unknown>,
  P extends string | undefined = undefined,
>(filters: T, prefix?: P): Prettify<CreateFiltersResult<T, P>> {
  prefix = (prefix || "") as P;
  const result: Record<string, unknown> = {};

  for (const [key, value] of Object.entries(filters)) {
    if (value == null || value === "") continue;
    result[uncapitalize(`${prefix}${capitalize(key)}`)] = value;
  }

  return result as CreateFiltersResult<T, P>;
}
